import { log } from "../../shared/services/debug-logger";
import {
  AuthContextProviderProps,
  AuthContextState,
  AuthProviderProps,
  AuthActions,
} from "../models/AuthContextModels";
import { datadogRum } from "@datadog/browser-rum";
import * as React from "react";

export const AuthContext = React.createContext<AuthContextProviderProps>(null);

const initialState: AuthContextState = {
  user: null,
};

function reducer(
  state: AuthContextState,
  action: {
    type: AuthActions;
    payload?: any;
  },
) {
  const { type, payload } = action;

  switch (type) {
    case "SET_USER":
      log("STATE - reducer - SET_USER", { hasAccessControl: !!payload?.accessControl });
      configRUMuser(payload);

      return { ...state, user: payload };
    default:
      return state;
  }
}

// Exclude RUM
// We can filter out our own traffic (dev team)
// For this to work, every new account that is added for testing purposes
// must have assigned DXP_Opslink_RoleDeveloper or DXP_Opslink_RoleTester
function configRUMuser(payload: any) {
  const { groups, sub } = payload.accessToken;
  const isDev = (groups as string[]).includes("DXP_Opslink_RoleDeveloper");
  const isQa = (groups as string[]).includes("DXP_Opslink_RoleTester");
  const isVO = (groups as string[]).includes("DXP_Opslink_RoleVesselOperator");
  const isVM = (groups as string[]).includes("DXP_Opslink_RoleVesselMaster");
  const isTES = (groups as string[]).includes("DXP_Opslink_RoleTradeExecutionSpecialist");
  const isRS = (groups as string[]).includes("DXP_Opslink_RoleOpsSupport");

  if (isDev) {
    datadogRum.setUser({
      role: "Developer",
      id: sub,
      email: sub,
    });
  }

  if (isQa) {
    datadogRum.setUser({
      role: "Tester",
      id: sub,
      email: sub,
    });
  }

  if (isVO) {
    datadogRum.setUser({
      role: "Vessel Operator",
      id: sub,
      email: sub,
    });
  }

  if (isVM) {
    datadogRum.setUser({
      role: "Vessel Master",
      id: sub,
      email: sub,
    });
  }

  if (isTES) {
    datadogRum.setUser({
      role: "Trade Execution Specialist",
      id: sub,
      email: sub,
    });
  }

  if (isRS) {
    datadogRum.setUser({
      role: "Ops Support",
      id: sub,
      email: sub,
    });
  }

  // TODO Label id, email, name, company
  // Needs to be double checked if it's permited according to company privacy policy
  // Customer portal uses such a setup
  // datadogRum.setUser({
  //   id: 'id',
  //   email: 'email',
  //   company: 'company',
  // });
}

export function AuthContextProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = React.useReducer(reducer, initialState);
  const userState = React.useMemo(() => ({ state, dispatch }), [state, dispatch]);

  return <AuthContext.Provider value={userState}>{children}</AuthContext.Provider>;
}

export function useAuthContext() {
  const context = React.useContext(AuthContext);

  if (!context) {
    throw new Error("useAuthContext must be used within a AuthContextProvider");
  }

  return context;
}
